import { ChevronLeft } from "lucide-react"
import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"

// IMP-518: the shared drawer shell, so the contact drawer and the job drawer
// are the same object rather than two that resemble each other.
//
// Medusa Drawer anatomy: the panel floats inset from the viewport with the
// overlay visible on every side, rounded and bordered all round — not flush to
// the edge like the default sheet. The close sits in the header band rather
// than floating over the content.

export function DrawerSheet({
  open,
  onOpenChange,
  className,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  className?: string
  children: ReactNode
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn(
          "inset-y-2 right-2 flex h-auto flex-col gap-0 overflow-hidden rounded-xl border p-0 ring-1 ring-black/10 sm:max-w-xl dark:ring-white/10",
          // The primitive's close is a bare 16px glyph at 70% opacity. Give it
          // the same box as any other icon button in the header band — size-8,
          // centred, rounded, with a real hover target — so it and the
          // overflow action are the same object at the same rhythm.
          "[&>button]:text-muted-foreground [&>button]:top-4 [&>button]:right-4 [&>button]:grid [&>button]:size-8 [&>button]:place-items-center [&>button]:rounded-md [&>button]:opacity-100",
          "[&>button:hover]:bg-accent [&>button:hover]:text-accent-foreground",
          className,
        )}
      >
        {children}
      </SheetContent>
    </Sheet>
  )
}

// The header band: title (with an optional badge beside it), an optional
// subtitle line, and an optional strip beneath holding the drawer's current
// state and its one control.
export function DrawerHeader({
  title,
  badge,
  subtitle,
  strip,
  action,
  back,
  className,
}: {
  title: string
  badge?: ReactNode
  subtitle?: ReactNode
  strip?: ReactNode
  // Sits just left of the primitive's close button, for overflow actions.
  action?: ReactNode
  // When a sub-view is pushed, the whole title block is replaced by this back
  // control — the header becomes the way out rather than carrying two of them.
  // The title stays in the DOM for the dialog's accessible name.
  back?: { label: string; onClick: () => void }
  className?: string
}) {
  if (back) {
    return (
      <SheetHeader className={cn("relative shrink-0 px-6 py-4", className)}>
        {action && <div className="absolute top-4 right-13 z-10">{action}</div>}
        <SheetTitle className="sr-only">{title}</SheetTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={back.onClick}
          className="text-muted-foreground -ml-3 h-8 w-fit cursor-pointer text-[13px]"
        >
          <ChevronLeft className="size-3.5 shrink-0" />
          {back.label}
        </Button>
      </SheetHeader>
    )
  }

  return (
    <SheetHeader
      className={cn("relative shrink-0 space-y-3 px-6 py-4", className)}
    >
      {/* The close button is rendered absolutely by the Sheet primitive at
          top-5/right-5; the action sits immediately left of it in the same
          band, rather than inline with the title. */}
      {action && <div className="absolute top-4 right-13 z-10">{action}</div>}
      <div className={cn("min-w-0", action ? "pr-20" : "pr-10")}>
        <div className="flex flex-wrap items-center gap-2">
          <SheetTitle className="text-lg">{title}</SheetTitle>
          {badge}
        </div>
        {subtitle && <div className="mt-1">{subtitle}</div>}
      </div>
      {strip}
    </SheetHeader>
  )
}

// The state strip: what's true right now, and the control that changes it.
// Used for a contact's outreach cycle and a job's active/paused state alike.
export function DrawerStrip({
  leading,
  title,
  detail,
  action,
}: {
  leading: ReactNode
  title: string
  detail?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="bg-muted/50 flex items-center gap-3.5 rounded-xl border p-3">
      {leading}
      <div className="min-w-0 flex-1">
        <p className="text-foreground text-[13px] font-medium">{title}</p>
        {detail}
      </div>
      {action}
    </div>
  )
}

// The bordered pill button the strips use for their single action.
export function DrawerStripButton({
  onClick,
  children,
}: {
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-muted-foreground hover:text-foreground hover:bg-background shrink-0 cursor-pointer rounded-md border px-2.5 py-1 text-xs font-medium transition-colors"
    >
      {children}
    </button>
  )
}
